import { OpenApiError } from '@zyno-io/openapi-client-codegen';

import { emailApiFieldError } from './email.helpers';

/**
 * Reading the message a Zyno API actually sent, for the statuses where that message IS the answer.
 *
 * The platform's services return errors as `{ error: string }`, but `configureVfOpenApiClient` only
 * converts **422** into a `UserError`. Everything else — the 403s, 404s and 409s that carry the most
 * useful sentences in the product ("Tenant is already associated with an MSP") — arrives as a plain
 * `OpenApiError`, and `formatError` then wraps it in "An application error has occurred… contact
 * support". So a self-explanatory business outcome reads to the user as a bug in the software.
 *
 * `hosting/helpers/errors.ts` solved this first and is still the reference for that module; this is
 * the same thing without the hosting scoping, for callers elsewhere. Worth collapsing the two if a
 * third copy ever appears.
 */
export function apiErrorStatus(err: unknown): number | undefined {
    return err instanceof OpenApiError ? err.response?.status : undefined;
}

export function apiErrorMessage(err: unknown, fallback = 'Something went wrong. Please try again.'): string {
    // Already turned into a `UserError` by the API client (see `configureZynoOpenApiClient`).
    const emailRejection = emailApiFieldError(err);
    if (emailRejection) return emailRejection.message;
    if (err instanceof OpenApiError) {
        const status = err.response?.status ?? 0;
        const body = err.body as { error?: unknown } | undefined;
        // 5xx bodies are a generic "Internal Server Error" — nothing a user can act on.
        if (status < 500 && typeof body?.error === 'string' && body.error) return body.error;
    }
    return fallback;
}

/**
 * The statuses a mutation form handles itself: a rejected value (400), a capability the caller
 * doesn't hold (403), a target that isn't there (404), and a state conflict (409).
 */
const EXPECTED_STATUSES = [400, 403, 404, 409];

/** Spread into a mutation's request options to keep its expected rejections out of Sentry. */
export const EXPECTED_MUTATION_ERRORS = { meta: { ignoreSentryStatuses: EXPECTED_STATUSES } };

/**
 * 424 Failed Dependency: a third party the endpoint depends on failed — a hardware distributor's
 * API, today. Neither our bug nor the caller's mistake, so it is worth showing and not worth
 * reporting. It is deliberately kept out of `EXPECTED_STATUSES`: a form that has not been written to
 * expect one should keep treating it as an unhandled fault rather than quietly relaying it.
 *
 * Only the server knows which dependency failed and why, so the answer is always to show the
 * sentence it sent — `apiErrorMessage(err, formatError(err))` — and never to compose one here.
 */
export const UPSTREAM_FAILURE_STATUS = 424;

export function isUpstreamFailure(err: unknown): boolean {
    return apiErrorStatus(err) === UPSTREAM_FAILURE_STATUS;
}

/** `EXPECTED_MUTATION_ERRORS` for a mutation that reaches a third party on the caller's behalf. */
export const EXPECTED_UPSTREAM_ERRORS = { meta: { ignoreSentryStatuses: [...EXPECTED_STATUSES, UPSTREAM_FAILURE_STATUS] } };

/**
 * Whether this is one of those expected rejections — i.e. it has already been shown to the user in
 * their own words and must NOT also go through `handleError`, which would report a working
 * validation path to Sentry as a fault.
 */
export function isExpectedApiError(err: unknown): boolean {
    const status = apiErrorStatus(err);
    return status !== undefined && EXPECTED_STATUSES.includes(status);
}
